import React, { useRef, useMemo, useState, useCallback } from "react";
import { connect } from "react-redux";
import {
    Column,
    DataGrid,
    FilterRow,
    FilterPanel,
    Sorting,
    Paging,
    Scrolling,
    Export,
    Pager,
    ColumnChooser,
    ColumnChooserSearch,
    Toolbar,
    Item,
    Editing,
    Selection,
} from "devextreme-react/data-grid";
import { createStore } from "devextreme-aspnet-data-nojquery";
import DataSource from "devextreme/data/data_source";
import { Button } from "devextreme-react/button";
import notify from "devextreme/ui/notify";
import { confirm } from "devextreme/ui/dialog";
import axios from "axios";
import RenderSelectBoxCell from "../../../common/RenderCell";
import { MESSAGES } from "../../../common/Messages";


const VectorStoreFilesTab = (props) => {
    const dataGridRef = useRef(null);
    const accessToken = props.oidc?.user?.access_token;
    const vectorStoreId = props.formFields?.Id || props.formFields?.id;
    const [selectedItemKeys, setSelectedItemKeys] = useState([]);

    const dataSource = useMemo(() => {
        if (!vectorStoreId) return null;
        const baseUrl = `${window._env_.REACT_APP_APIS}/ai/vectorStore`;
        const store = createStore({
            key: "Id",
            loadUrl: `${baseUrl}/GetVectorStoreFiles`,
            insertUrl: `${baseUrl}/AddVectorStoreFile`,
            loadParams: {
                vectorStoreId: vectorStoreId,
            },
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.headers = {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                };
            },
        });
        return new DataSource({ store });
    }, [vectorStoreId, accessToken]);

    const onSelectionChanged = useCallback((e) => {
        setSelectedItemKeys(e.selectedRowKeys || []);
    }, []);

    const deleteRecords = useCallback(() => {
        const result = confirm(`<i>${MESSAGES.DELETE_CONFIRM}</i>`, "Delete");
        result.then((dialogResult) => {
            if (dialogResult) {
                dataGridRef.current.instance().beginCustomLoading("Deleting");
                const deleteUrl = `${window._env_.REACT_APP_APIS}/ai/vectorStore/DeleteVectorStoreFile`;
                axios.post(deleteUrl, 
                    {
                        VectorStoreId: vectorStoreId,
                        Id: selectedItemKeys[0],
                    }, 
                    {
                    headers: {
                            "Content-Type": "application/json",
                            access_token: accessToken,
                            Authorization: `Bearer ${accessToken}`,
                        }
                    })
                    .then(() => {
                        dataGridRef.current.instance().endCustomLoading();
                        dataGridRef.current.instance().refresh();
                        notify({ message: "File removed from vector store", width: 100 }, "success", 3000);
                    })
                    .catch((err) => {
                        dataGridRef.current.instance().endCustomLoading();
                        notify(err?.response?.data?.message || err?.message || "Error", "error", 5000);
                    });
            }
        });
    }, [selectedItemKeys, vectorStoreId, accessToken]);

    const onRefresh = () => {
        if (dataGridRef.current?.instance()) {
            dataGridRef.current.instance().refresh();
        }
    };

    const onRowInserting = (e) => {
        e.data.VectorStoreId = vectorStoreId;
    };

    const onCellClick = (e) => {
        if (e.rowType === "data" && e.data?.Id) {
            e.component.selectRows([e.data.Id], false);
        }
    };

    if (!vectorStoreId) {
        return (
            <div className="p-3">
                <p className="text-muted">Save or open a vector store to view its files.</p>
            </div>
        );
    }

    return (
        <div>
            <DataGrid
                ref={dataGridRef}
                dataSource={dataSource}
                selectedRowKeys={selectedItemKeys}
                onSelectionChanged={onSelectionChanged}
                onCellClick={onCellClick}
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                height="80vh"
                onExporting={(e) => {
                    if (window.handleDevExtremeDataGridExport) {
                        window.handleDevExtremeDataGridExport(e);
                    }
                }}
                onRowInserting={onRowInserting}
                onRowInserted={() => {
                    notify({ message: "File added to vector store", width: 100 }, "success", 3000);
                }}
            >
                <Selection mode="single" />
                <Editing
                    mode="cell"
                    allowAdding={true}
                />
                <Scrolling mode="standard" />
                <Sorting visible={true} mode="multiple" />
                <FilterRow visible={true} />
                <FilterPanel visible={true} />
                <Paging enabled={false} />
                <Export enabled={true} allowExportSelectedData={true} />
                <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                    <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: "Search column" }} />
                </ColumnChooser>

                <Column dataField="Id" caption="File Id" dataType="string" allowFiltering={true} allowEditing={false} />
                <Column dataField="FileName" caption="File Name" dataType="string" allowFiltering={true} editCellRender={(cellElement) =>
                    RenderSelectBoxCell({
                        loadParams: {
                            lookupType: function () {
                                return "None";
                            }
                        },
                        loadUrl: `${window._env_.REACT_APP_APIS}/ai/file/GetFiles`,
                        key: "Id",
                        displayExpr: "FileName",
                        valueExpr: "Id",
                        accessToken: accessToken,
                        defaultValue: cellElement.data.FileName,
                        sort: [{ selector: "FileName", desc: false }],
                        onValueChangeCallback: (value, text) => {
                            cellElement.setValue(({ value, text }));
                        },
                    })
                }
                    setCellValue={(rowData, newValue) => {
                        rowData.FileName = newValue.text;
                        rowData.Id = newValue.value;
                    }} />
                <Column dataField="CreatedAt" caption="Created At" dataType="number" allowFiltering={true} allowEditing={false} />
                <Column dataField="Status" caption="Status" dataType="string" allowFiltering={true} allowEditing={false} />
                <Column dataField="UsageBytes" caption="Usage (bytes)" dataType="number" format="#,##0" allowFiltering={true} allowEditing={false} />
                <Column
                    dataField="LastError.Code"
                    caption="Last Error Code"
                    dataType="string"
                    allowFiltering={true}
                    allowEditing={false}
                />
                <Column
                    dataField="LastError.Message"
                    caption="Last Error Message"
                    dataType="string"
                    allowFiltering={true}
                    allowEditing={false}
                />

                <Toolbar>
                    <Item name="addRowButton" disabled={props.formFields?.Id == "vs_6823d73320088191bec8ead37a63edd2"} />
                    <Item location="after">
                        <Button
                            onClick={deleteRecords}
                            icon="trash"
                            disabled={!selectedItemKeys.length || props.formFields?.Id == "vs_6823d73320088191bec8ead37a63edd2"}
                        />
                    </Item>
                    <Item location="after">
                        <Button icon="refresh" onClick={onRefresh} className="accordianButton" />
                    </Item>
                    <Item name="exportButton" />
                    <Item name="columnChooserButton" />
                </Toolbar>
            </DataGrid>
        </div>
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(VectorStoreFilesTab);
